/**
 * Bulk contact actions for the copilot's contacts table.
 *
 * Every action the Contacts page offers goes through ONE endpoint
 * (/cm/bulk/perform) discriminated by `type`, so this is a single payload
 * builder rather than one function per action. The payload shape is copied
 * from ContactModule's getBulkPerformActionPayload deliberately — the backend
 * reads `filter.contactIds` when specific rows are ticked and `search` when
 * the user picked "all N matching", and getting that wrong silently acts on
 * the wrong population.
 */

import { makeApi, URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import { CLIENT_EVENTS } from "./agentContract";


/**
 * @param type    bulk operation, e.g. ENRICH_CONTACT / ADD_TO_SEQUENCE / ADD_TAG
 * @param table   the copilot table entry (selection + the search behind it)
 * @param extra   action payload -> `data` (sequenceId, contactTags, ...)
 */
/**
 * True when `search` actually narrows anything. The orchestrator always sends a
 * criteria object, so a null-check is not enough: an unfiltered list produces
 * {stageId: undefined, searchTerm: "", listId: undefined, filter: {}}, which
 * the server reads as EVERY contact in the account. Acting on "all N matching"
 * with that is a whole-account enrich/enrol/export.
 */
const hasRealCriteria = search => {
  if (!search || typeof search !== "object") return false;
  const filter = search.filter && typeof search.filter === "object" ? search.filter : {};
  return Boolean(
    search.stageId ||
      (search.searchTerm || "").trim() ||
      search.listId ||
      Object.keys(filter).length
  );
};

export const buildBulkPayload = (type, table, extra = null) => {
  if (!type || !table) return null;
  const { selectedIds = [], allMatchingSelected = false, search = null } =
    table || {};
  const ids = selectedIds.slice();
  // Nothing ticked and not in select-all mode: refuse rather than send a
  // payload the server would read as "everything".
  if (!allMatchingSelected && !ids.length) return null;
  // Same refusal for the other direction: "all matching" with criteria that
  // match everything would act on the whole account.
  if (allMatchingSelected && !hasRealCriteria(search)) return null;

  const criteria = search || {};
  const payload = {
    source: "CONTACT",
    type,
    totalCount: allMatchingSelected ? table.totalCount || 0 : ids.length,
    // `all` means the selection is defined by the search, not by ids.
    all: allMatchingSelected,
    search: {
      stageId: criteria.stageId,
      searchTerm: criteria.searchTerm || "",
      filter: criteria.filter || {},
      listId: criteria.listId
    },
    filter: {
      ...(allMatchingSelected ? {} : { contactIds: ids })
    }
  };
  // Enrich is the one action whose arguments do NOT ride in `data` — the
  // Contacts page sends `enrichContactPayload`, and the server reads only that.
  if (extra) {
    if (type === "ENRICH_CONTACT") payload.enrichContactPayload = extra;
    else payload.data = extra;
  }
  return payload;
};

/** POST a bulk action. Returns the response body, or null on failure. */
export const performContactBulkAction = async (type, table, extra = null) => {
  const data = buildBulkPayload(type, table, extra);
  if (!data) return null;
  const response = await makeApi({
    url: URLS.performContactsBulkOperation,
    method: "POST",
    data
  });
  return (response && response.data) || null;
};

/** CRM exports use their own endpoint but the same payload. */
export const exportContactsToCrm = async (type, table, crmEntityType = "") => {
  const data = buildBulkPayload(type, table);
  if (!data) return null;
  if (crmEntityType) data.data = { crmEntityType: crmEntityType.toUpperCase() };
  const response = await makeApi({
    url: URLS.exportContactsToHubspot,
    method: "POST",
    data
  });
  return (response && response.data) || null;
};

/**
 * Prospect-result actions (find_people rows, served live from AI-Ark).
 *
 * These are NOT records the account owns, and they are NOT in `master_contact`,
 * so every id-keyed route (the Discover page's purchaseContact) resolves to
 * nothing. The List Builder — which searches the same AI-Ark source — instead
 * posts the RECORDS to /cm/contacts/ ("add to directory"), keyed on the
 * LinkedIn URL, with email null so nothing is revealed and no email credits are
 * spent. It is metered by the separate SN contact-add allowance.
 *
 * This mirrors that payload exactly; diverging from it is what broke before.
 */
const linkedinKey = row => {
  const raw = (row && (row.linkedIn || row.linkedin || row.link)) || "";
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return "";
  return Utils.removeHttpFromString(trimmed).trim() || trimmed;
};

/** Rows that can actually be added — the backend drops the rest. */
export const addableProspects = rows => (rows || []).filter(r => linkedinKey(r));

export const buildAddToDirectoryPayload = (rows, listId) => ({
  contacts: addableProspects(rows).map(row => ({
    firstName: row.firstName || null,
    lastName: row.lastName || null,
    company: row.company || null,
    domain: row.domain || null,
    jobTitle: row.title || null,
    industry: row.industry || null,
    linkedinId: linkedinKey(row),
    // Never a reveal: adding to the directory must not spend email credits.
    email: null,
    city: row.city || null,
    state: row.state || null,
    country: row.country || null,
    liCompanyId: row.companyLinkedIn || null,
    aiArkId: row.aiArkId || null
  })),
  contactTags: [],
  listId: listId != null ? String(listId) : null,
  useSnCredits: true
});

/**
 * Add selected prospects to a contact list.
 *
 * Sent as a client EVENT so the orchestrator makes the call: it runs the same
 * /v1/contact-lists/{id}/prospects request the agent's save tool makes, minus
 * the model round trip. That endpoint upserts on LinkedIn public id, so a
 * prospect already in the directory is still attached to the list — the
 * /cm/contacts/ route classifies those as duplicates and silently leaves them
 * out — and it bills SN contact credits for what it actually added.
 */
export const addProspectsToList = (transport, blockId, listId, rows) => {
  const addable = addableProspects(rows);
  if (!transport || !listId || !addable.length) return 0;
  transport.send({
    type: CLIENT_EVENTS.ADD_PROSPECTS_TO_LIST,
    blockId,
    listId,
    rows: addable.map(row => ({
      name: row.name,
      firstName: row.firstName,
      lastName: row.lastName,
      title: row.title,
      company: row.company,
      domain: row.domain,
      linkedIn: row.linkedIn
    }))
  });
  return addable.length;
};

/**
 * A throwaway list used purely to scope one export.
 *
 * Exporting by list is the only way to export prospects (they have no local
 * record until they are added), and exporting a list the user already owns
 * would sweep in every contact already in it. So the export gets its own list,
 * which is removed afterwards — the contacts themselves stay in the account.
 */
export const createExportList = async () => {
  const stamp = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
  const response = await makeApi({
    url: URLS.createContactList,
    method: "POST",
    data: { listName: `Copilot export ${stamp}` }
  });
  const list = response?.data?.listResponse?.list?.[0];
  return list && list.id ? list : null;
};

/** Remove the throwaway list. The contacts it held are unaffected. */
export const deleteExportList = async listId => {
  if (!listId) return;
  try {
    await makeApi({
      url: `${URLS.deleteContactList}/${listId}`,
      method: "DELETE"
    });
  } catch (err) {
    // A leftover empty list is untidy, not broken — never fail the export for it.
  }
};

/**
 * Export every contact in a list, using the normal contact export.
 *
 * Prospects have no local record until they are added, so a prospect export is
 * two steps: add them (which creates the contacts and bills SN credits), then
 * export exactly those ids. Same bulk operation the Contacts page runs, so the
 * file has the full field set, email included.
 */
/**
 * Turns /cm/export/contact's answer into the {emailed}|{downloaded}|null
 * contract both export callers branch on. Deliberately free of toasts and
 * popups: exportListAfterAdd owns those for the prospect flow.
 */
const handleExportResponseBody = body => {
  if (!body) return null;
  const { type = "", contactData } = body;
  if (type === "limit_reached") {
    // Too many to return inline; the backend emails the file instead.
    return { emailed: true };
  }
  // A destructuring default does not fire for null, and a non-array body would
  // report a download that never happened.
  if (!Array.isArray(contactData) || !contactData.length) return null;

  // Same mapper the product uses, so the columns match every other export.
  Utils.constructExportToCsvData(contactData, link => {
    if (link) Utils.downloadExportedContact(link);
  });
  return { downloaded: true };
};

export const exportContactsByIds = async contactIds => {
  if (!contactIds || !contactIds.length) return null;
  // The Contacts page's own CSV export: POST /cm/export/contact with the ids,
  // which answers with the records to write. NOT /cm/bulk/perform — there is no
  // CONTACT_EXPORT in BulkOperationType, so that call could only ever fail.
  const response = await makeApi({
    url: URLS.exportLimitedContact,
    method: "POST",
    data: { contactList: contactIds, totalCount: contactIds.length }
  });
  return handleExportResponseBody((response && response.data) || null);
};

/**
 * Saved contacts already exist as records, so unlike prospects they need no
 * temporary list — the ids (or the search behind "all N matching") go straight
 * to the same endpoint the Contacts page uses.
 *
 * Exported for its own sake so the payload can be asserted without a network.
 */
export const buildSavedExportPayload = table => {
  if (!table) return null;
  const { selectedIds = [], allMatchingSelected = false, search = null } = table;
  const ids = selectedIds.slice();

  if (allMatchingSelected) {
    // Criteria that narrow nothing would make the backend fall through to
    // exporting the whole team. Refuse instead.
    if (!hasRealCriteria(search)) return null;
    return {
      totalCount: table.totalCount || 0,
      search: {
        stageId: search.stageId,
        searchTerm: search.searchTerm || "",
        filter: search.filter || {},
        listId: search.listId
      }
    };
  }
  if (!ids.length) return null;
  // A non-empty contactList wins server-side, so the search is omitted here.
  return { contactList: ids, totalCount: ids.length };
};

export const exportSavedContacts = async table => {
  const data = buildSavedExportPayload(table);
  if (!data) return null;
  const response = await makeApi({
    url: URLS.exportLimitedContact,
    method: "POST",
    data
  });
  return handleExportResponseBody((response && response.data) || null);
};

/** How many contacts an action will touch — what the confirmation should say. */
export const selectionCount = table => {
  if (!table) return 0;
  return table.allMatchingSelected
    ? table.totalCount || 0
    : (table.selectedIds || []).length;
};

export default performContactBulkAction;
